/**
 * Ordering of Plant Viewer flow lists along their directed
 * `FEEDS` edges, with an explicit coverage verdict so the UI can tell
 * a real process order apart from the alphabetical fallback instead of
 * silently rendering the fallback as if it were a flow.
 */

/**
 * How completely the `FEEDS` edges describe an order over the items:
 *   - `'full'`      — the edges chain every item into a single linear
 *                     path (or there is at most one item, so there is
 *                     no order to describe).
 *   - `'branching'` — every item participates in a `FEEDS` edge, but
 *                     the edges branch, merge, or split into segments
 *                     rather than forming one chain; the returned
 *                     order is a stable topological order.
 *   - `'partial'`   — some relevant edges exist but some items have no
 *                     `FEEDS` edge at all; the returned order is a
 *                     stable topological order with unconnected items
 *                     interleaved in the caller's input order.
 *   - `'none'`      — no edge connects two of the items; input order
 *                     kept.
 */
export type FeedsCoverage = 'full' | 'branching' | 'partial' | 'none'

export interface FeedsOrderResult<T> {
  ordered: T[]
  coverage: FeedsCoverage
}

/**
 * Order `items` along their directed `FEEDS` edges. Edges whose
 * endpoints are not both in `items` are ignored (as are self-loops and
 * duplicate source→target pairs).
 *
 * A single linear chain covering every item returns that walk with
 * coverage `'full'` — the only verdict the UI renders as a caret-
 * separated flow. Real plant lines are rarely a pure pipeline (reject
 * streams fan out, supply lines fan in), so any other edge topology is
 * ordered with a stable topological sort (Kahn's algorithm; ties and
 * cycle breaks resolve to the earliest input position, so the
 * alphabetical input keeps the output deterministic) and the coverage
 * verdict tells the caller whether every item took part in the flow
 * (`'branching'`) or some items are unconnected (`'partial'`).
 */
export function orderByFeeds<T extends { id: string }>(
  items: T[],
  feeds: Array<{ source: string; target: string }>
): FeedsOrderResult<T> {
  if (items.length <= 1) return { ordered: items, coverage: 'full' }
  const idx = new Map(items.map((s, i) => [s.id, i]))
  const successors = new Map<string, string[]>()
  const inDegree = new Map<string, number>()
  const outDegree = new Map<string, number>()
  const touched = new Set<string>()
  const seenPairs = new Set<string>()
  for (const edge of feeds) {
    if (!idx.has(edge.source) || !idx.has(edge.target)) continue
    if (edge.source === edge.target) continue
    const pair = `${edge.source}\u0000${edge.target}`
    if (seenPairs.has(pair)) continue
    seenPairs.add(pair)
    const list = successors.get(edge.source)
    if (list) list.push(edge.target)
    else successors.set(edge.source, [edge.target])
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1)
    outDegree.set(edge.source, (outDegree.get(edge.source) ?? 0) + 1)
    touched.add(edge.source)
    touched.add(edge.target)
  }
  if (seenPairs.size === 0) return { ordered: items, coverage: 'none' }

  // Single-successor chain walk: only a topology where no node fans out
  // or fans in can be a linear pipeline worth rendering with carets.
  const isChainShaped =
    items.every((s) => (outDegree.get(s.id) ?? 0) <= 1) &&
    items.every((s) => (inDegree.get(s.id) ?? 0) <= 1)
  if (isChainShaped) {
    const start = items.find((s) => !inDegree.has(s.id)) ?? items[0]
    const seq: T[] = [start]
    const seen = new Set<string>([start.id])
    let cur = start.id
    for (;;) {
      const nextId = successors.get(cur)?.[0]
      if (!nextId || seen.has(nextId)) break
      const node = items.find((s) => s.id === nextId)
      if (!node) break
      seq.push(node)
      seen.add(nextId)
      cur = nextId
    }
    if (seq.length === items.length) return { ordered: seq, coverage: 'full' }
  }

  // Stable topological order (Kahn). Lists here are small (a line's
  // steps), so the O(n^2) earliest-input-position scan is fine.
  const remaining = new Set(items.map((s) => s.id))
  const pending = new Map(inDegree)
  const ordered: T[] = []
  while (remaining.size > 0) {
    const pick =
      items.find((s) => remaining.has(s.id) && (pending.get(s.id) ?? 0) === 0) ??
      // Every remaining node sits on a cycle — break it at the earliest
      // input position so the output stays deterministic.
      items.find((s) => remaining.has(s.id))
    if (!pick) break
    remaining.delete(pick.id)
    ordered.push(pick)
    for (const target of successors.get(pick.id) ?? []) {
      if (remaining.has(target)) pending.set(target, (pending.get(target) ?? 0) - 1)
    }
  }
  return {
    ordered,
    coverage: touched.size === items.length ? 'branching' : 'partial'
  }
}
